import { ChevronDown } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { FieldWrapper } from "./FieldWrapper";

interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  label?: string;
  helper?: string;
  error?: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
}

export default function SearchableSelect({
  label,
  helper,
  error,
  required,
  value,
  onChange,
  options,
  placeholder = "Search...",
  className = "",
  id,
  disabled = false,
}: SearchableSelectProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const containerRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);

  const selectedOption = options.find((o) => o.value.toLowerCase() === value.trim().toLowerCase());
  const selectedLabel = selectedOption?.label ?? value;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [options, query]);

  useEffect(() => {
    setHighlighted(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  function selectOption(opt: SearchableSelectOption) {
    onChange(opt.value);
    setQuery("");
    setOpen(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlighted]) selectOption(filtered[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  }

  return (
    <FieldWrapper label={label} helper={helper} error={error} required={required} htmlFor={fieldId}>
      <div className="relative" ref={containerRef}>
        <input
          id={fieldId}
          type="text"
          role="combobox"
          disabled={disabled}
          aria-expanded={open && !disabled}
          aria-autocomplete="list"
          autoComplete="off"
          value={open && !disabled ? query : selectedLabel}
          onChange={(e) => {
            if (disabled) return;
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            if (disabled) return;
            setOpen(true);
            setQuery("");
          }}
          onClick={() => {
            if (disabled) return;
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`h-[42px] w-full rounded-lg border pr-10 pl-3.5 text-sm placeholder:text-ink-muted focus:outline-none focus:ring-2 ${
            disabled
              ? "border-slate-200 bg-slate-100 text-ink-muted cursor-not-allowed"
              : error
                ? "border-danger bg-white text-ink focus:ring-danger/30"
                : "border-slate-300 bg-white text-ink focus:border-primary-500 focus:ring-primary-500/30"
          } ${className}`}
          aria-invalid={!!error}
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center text-ink-muted">
          <ChevronDown className="h-4 w-4" />
        </span>

        {open && !disabled && (
          <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
            {filtered.length === 0 ? (
              <p className="px-3.5 py-2 text-sm text-ink-muted">No matches found</p>
            ) : (
              filtered.map((opt, i) => (
                <button
                  key={opt.value}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectOption(opt)}
                  className={`flex w-full items-center px-3.5 py-2 text-left text-sm cursor-pointer ${
                    opt.value === value
                      ? "bg-primary-50 font-medium text-primary-600"
                      : i === highlighted
                        ? "bg-slate-50 text-ink"
                        : "text-ink hover:bg-slate-50"
                  }`}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </FieldWrapper>
  );
}
